import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { colors, authColors } from '../config/branding';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-mode');
    return (saved as ThemeMode) || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = createTheme({
   palette: {
  mode,
  primary: {
    main: '#0052CC',
    light: '#2684FF',
    dark: '#0747A6',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#6554C0',
    light: '#8777D9',
    dark: '#403294',
    contrastText: '#ffffff',
  },
  background: {
    // MODE SOMBRE : Fini le noir/gris foncé. On passe sur un bleu-gris acier lumineux et pro.
    // MODE CLAIR : Un blanc/gris pur très propre.
    default: mode === 'dark' ? '#1D2A44' : '#FAFBFC', 
    paper: mode === 'dark' ? '#2A3B5C' : '#FFFFFF',
  },
  text: {
    // Mode sombre : Texte blanc cassé très lisible. Mode clair : Bleu nuit Atlassian.
    primary: mode === 'dark' ? '#F4F5F7' : '#172B4D', 
    secondary: mode === 'dark' ? '#B3BAC5' : '#5E6C84',
  },
  divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#DFE1E6',
  action: {
    hover: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(9, 30, 66, 0.04)',
    selected: mode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(9, 30, 66, 0.08)',
    disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.38)',
    disabledBackground: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.12)',
  },
  // ... success, warning, error restent identiques
},
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'dark' ? authColors.background : '#f0f2f5',
            backgroundImage: mode === 'dark'
              ? 'repeating-linear-gradient(90deg, rgba(217,217,217,0.02) 0px, rgba(217,217,217,0.02) 1px, transparent 1px, transparent 20px), repeating-linear-gradient(0deg, rgba(217,217,217,0.02) 0px, rgba(217,217,217,0.02) 1px, transparent 1px, transparent 20px)'
              : 'none',
          },
          '*, *::before, *::after': {
            scrollbarWidth: 'thin',
            scrollbarColor: mode === 'dark'
              ? 'rgba(255,255,255,0.14) rgba(255,255,255,0.04)'
              : 'rgba(0,0,0,0.18) rgba(0,0,0,0.04)',
          },
          '*::-webkit-scrollbar': {
            width: 6,
            height: 6,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            borderRadius: 3,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.18)',
            borderRadius: 3,
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.30)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            fontWeight: 600,
            padding: '10px 20px',
          },
          contained: {
            boxShadow: mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.25)' : '0 2px 10px rgba(74,144,226,0.25)',
            '&:hover': {
              boxShadow: mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.35)' : '0 4px 14px rgba(74,144,226,0.35)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: mode === 'dark' ? `1px solid ${authColors.border}` : '1px solid rgba(0,0,0,0.12)',
            boxShadow: mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
          },
        },
      },
     MuiTextField: {
  styleOverrides: {
    root: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 4,
        backgroundColor: mode === 'dark' ? '#243452' : '#FAFBFC', 
        '& fieldset': {
          borderWidth: '1px',
          borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : '#DFE1E6',
        },
        '&:hover fieldset': {
          borderColor: mode === 'dark' ? '#2684FF' : '#A5ADBA',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#0052CC',
        },
      },
    },
  },
},
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(74,144,226,0.08)',
              fontWeight: 700,
              color: mode === 'dark' ? authColors.text : '#2c3e50',
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(74,144,226,0.04)',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            boxShadow: mode === 'dark' ? '8px 0 32px rgba(0,0,0,0.4)' : '4px 0 20px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
